import { useCallback, useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, money, shortDate } from "@/lib/api";
import { StaffLayout } from "@/components/StaffLayout";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";

const FILTERS = ["all", "unpaid", "overdue", "paid"];

export default function StaffInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => toast.error("Unable to load invoices"));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (inv, status) => {
    try {
      await api.patch(`/invoices/${inv.id}`, null, { params: { status } });
      toast.success(`${inv.number} marked ${status}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const rows = filter === "all" ? invoices : invoices.filter((i) => i.status === filter);
  const total = rows.reduce((sum, i) => sum + i.amount, 0);

  return (
    <StaffLayout>
      <div className="flex flex-wrap items-end gap-5">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-gold">Billing</p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl text-[#F3F4F6]">Invoice management</h1>
        </div>
        <div className="ml-auto flex gap-3">
          <Button
            data-testid="export-financial-csv-btn"
            variant="outline"
            className="rounded-full border-white/20 bg-transparent hover:bg-white/10"
            onClick={() => downloadFile("/reports/export", "vineford-financial.csv", { kind: "financial", format: "csv" })}
          >
            <Download className="h-4 w-4 mr-2 text-gold" /> CSV
          </Button>
          <Button
            data-testid="export-financial-pdf-btn"
            variant="outline"
            className="rounded-full border-white/20 bg-transparent hover:bg-white/10"
            onClick={() => downloadFile("/reports/export", "vineford-financial.pdf", { kind: "financial", format: "pdf" })}
          >
            <FileText className="h-4 w-4 mr-2 text-gold" /> PDF
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`invoice-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-1.5 text-xs uppercase tracking-wider transition-colors ${
              filter === f ? "border-gold/50 bg-gold/15 text-gold" : "border-white/15 text-[#9CA3AF] hover:text-[#F3F4F6]"
            }`}
          >
            {f}
          </button>
        ))}
        <p className="ml-auto text-sm text-[#9CA3AF]">
          {rows.length} invoices · <span className="text-gold font-display text-lg">{money(total)}</span>
        </p>
      </div>

      <div className="glass rounded-2xl overflow-hidden divide-y divide-white/5" data-testid="invoice-table">
        {rows.map((inv) => (
          <div key={inv.id} className="flex flex-wrap items-center gap-4 px-6 py-4" data-testid="staff-invoice-row">
            <div className="min-w-[220px] flex-1">
              <p className="text-sm text-[#F3F4F6]">{inv.number}</p>
              <p className="text-xs text-[#6B7280]">
                {inv.case_reference} · {inv.client_name} · due {shortDate(inv.due_date)}
              </p>
            </div>
            <p className="font-display text-lg text-gold">{money(inv.amount, inv.currency)}</p>
            <StatusPill value={inv.status} />
            <select
              data-testid="staff-invoice-status-select"
              value={inv.status}
              onChange={(e) => setStatus(inv, e.target.value)}
              className="rounded-full bg-white/5 border border-white/15 px-3 py-1.5 text-xs text-[#F3F4F6]"
            >
              <option value="unpaid">unpaid</option>
              <option value="paid">paid</option>
              <option value="overdue">overdue</option>
            </select>
            <Button
              data-testid="staff-invoice-pdf-btn"
              size="sm"
              variant="ghost"
              className="rounded-full text-[#D1D5DB] hover:bg-white/10 hover:text-gold"
              onClick={() => downloadFile(`/invoices/${inv.id}/pdf`, `${inv.number}.pdf`).catch(() => toast.error("Download failed"))}
            >
              <Download className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </div>
        ))}
        {!rows.length ? <p className="px-6 py-8 text-sm text-[#9CA3AF]">No invoices match this filter.</p> : null}
      </div>
    </StaffLayout>
  );
}
